/* The Clara Documents page's list: the query it sends, how a type reads, and the banner of
   documents Clara is unsure of. The server pages, searches, filters and sorts (about 2000
   documents at a site); the browser only holds the page on screen. */

// Mirrors clara/documents.py DOCUMENT_TYPES; the service validates, this renders.
export const DOCUMENT_TYPES = ["Validated Policy", "Validated Procedure", "Guideline", "Information", "Evidence"]

export const PAGE_SIZE = 50
export const BANNER_ALL = 200

export function listQuery(workspace, { q, type, status, review, original, group, sort, order, page, perPage = PAGE_SIZE, ids, counts = true } = {}) {
	const params = new URLSearchParams({ workspace_name: workspace })
	if (q?.trim()) params.set("q", q.trim())
	if (type) params.set("type", type)
	if (status) params.set("status", status)
	if (review) params.set("review", review)
	if (original) params.set("original", original)
	if (group?.length) params.set("group", [].concat(group).join(","))
	if (sort) params.set("sort", sort)
	if (order) params.set("order", order)
	if (ids) params.set("ids", ids.join(","))
	if (!counts) params.set("counts", "0")
	params.set("page", String(page || 1))
	params.set("per_page", String(perPage))
	return params.toString()
}

// "auto" types are Clara's confident reading; a person's choice reads plainly. Clara reads
// the type while indexing, so until then an unchosen type is still being detected.
export function typeLabel(document) {
	if (["pending", "indexing"].includes(document?.status) && document?.type_source !== "manual") {
		return { tone: "detecting", text: "Detecting type…" }
	}
	if (document?.needs_type) return { tone: "guess", text: document.type_guess ? `Unsure: ${document.type_guess}?` : "Needs a type" }
	if (DOCUMENT_TYPES.includes(document?.doc_type)) {
		return document.type_source === "auto"
			? { tone: "auto", text: document.doc_type, hint: "Detected by Clara" }
			: { tone: "manual", text: document.doc_type }
	}
	return { tone: "none", text: "Unclassified" }
}

// The banner's choice for each row: what the admin picked, else Clara's best guess.
export function bannerChoice(document, choices = {}) {
	return choices[document.id] ?? document.type_guess ?? ""
}

export function confirmAssignments(rows, choices = {}) {
	return rows
		.map(document => ({ document_id: document.id, doc_type: bannerChoice(document, choices) }))
		.filter(assignment => DOCUMENT_TYPES.includes(assignment.doc_type))
}

// Mirrors clara/documents.py STATUS_GROUPS: every document is in exactly one, the first that applies.
export const STATUS_GROUPS = [
	{ key: "queued", label: "Being read", hint: "Clara is reading these now" },
	{ key: "failed", label: "Could not read", hint: "Each one shows the reason" },
	{ key: "type", label: "Needs a type", hint: "Clara is not sure what these are" },
	{ key: "overdue", label: "Review overdue", hint: "Answers that quote these carry a warning" },
	{ key: "pdf", label: "No PDF", hint: "Citations cannot open the file" },
	{ key: "ready", label: "Ready", hint: "" },
]
export const ATTENTION = ["failed", "type", "overdue", "pdf"]

export function views(counts = {}, active = "all") {
	const groups = counts.groups ?? {}
	const byType = counts.by_type ?? {}
	return [
		{ key: "all", label: "All", count: counts.total ?? 0 },
		{ key: "attention", label: "Needs attention", count: ATTENTION.reduce((sum, key) => sum + (groups[key] ?? 0), 0) },
		...DOCUMENT_TYPES.filter(t => byType[t] > 0 || t === active).map(t => ({ key: t, label: t, count: byType[t] ?? 0 })),
	]
}

export function listGroups(groupBy, view, originalsStored) {
	const viewQuery = view === "attention" ? { group: ATTENTION } : DOCUMENT_TYPES.includes(view) ? { type: view } : {}
	if (groupBy === "type") {
		const types = DOCUMENT_TYPES.includes(view) ? [view] : DOCUMENT_TYPES
		return [
			...types.map(t => ({ key: t, label: t, icon: "doc", query: { ...viewQuery, type: t } })),
			...(DOCUMENT_TYPES.includes(view) ? [] : [
				{ key: "needs_type", label: "Needs a type", icon: "type", query: { ...viewQuery, type: "needs_type" } },
				{ key: "untyped", label: "Unclassified", icon: "doc", query: { ...viewQuery, type: "untyped" } },
			]),
		]
	}
	if (groupBy === "none") return [{ key: "all", label: "All documents", icon: "doc", query: viewQuery, flat: true }]
	return STATUS_GROUPS
		.filter(g => (view === "attention" ? ATTENTION.includes(g.key) : true) && (g.key !== "pdf" || originalsStored))
		.map(g => ({ ...g, icon: g.key, query: { ...viewQuery, group: [g.key] } }))
}

function daysBetween(iso, today) {
	const [year, month, day] = iso.split("-").map(Number)
	const then = Date.UTC(year, month - 1, day)
	const now = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate())
	return Math.round((then - now) / 86400000)
}

function span(days) {
	if (days < 60) return `${days} day${days === 1 ? "" : "s"}`
	const months = Math.round(days / 30.44)
	if (months < 12) return `${months} months`
	const years = Math.floor(months / 12)
	const rest = months % 12
	return `${years} year${years === 1 ? "" : "s"}${rest ? ` ${rest} month${rest === 1 ? "" : "s"}` : ""}`
}

export function statusOf(document, originalsStored) {
	if (["pending", "indexing"].includes(document?.status)) return "queued"
	if (document?.status === "failed") return "failed"
	if (document?.needs_type) return "type"
	if (document?.review_status === "overdue") return "overdue"
	if (originalsStored && !document?.has_original) return "pdf"
	return "ready"
}

export function reviewDistance(iso, today = new Date()) {
	if (!iso) return ""
	const days = daysBetween(iso, today)
	if (days < 0) return `Overdue by ${span(-days)}`
	if (days === 0) return "Due today"
	return `Due in ${span(days)}`
}

// Shown in the row only when there is something to say: a ready document has no status.
export function statusLabel(document) {
	switch (document?.status) {
		case "pending": return { tone: "pending", text: "Queued" }
		case "indexing": return { tone: "pending", text: "Indexing" }
		case "failed": return { tone: "failed", text: "Failed" }
		default: return null
	}
}

export function shortDay(iso) {
	if (!iso) return ""
	return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" })
}
